import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {concatMap, map, Observable, of, switchMap, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { StringUtils } from '../classes/stringUtils';
import {
  IPromptDefaults,
  SUGGESTED_EXPLANATION_DIFFICULTY,
  SUGGESTED_FORMATS, SUGGESTED_LANGUAGES, SUGGESTED_RESULT_LENGTHS, SUGGESTED_ROLES, SUGGESTED_TONES
} from '../constants/defaults';
import { AIApps } from '../types/AIApps';
import { IClientConfig, INoteTakerMeta } from '../types/client-config';
import { IAIStatus } from '../types/IAIStatus';
import {IAwesomeDocsConfig} from '../types/IAwesomeDocs';
import { IDataToolsConfig } from '../types/IDataTools';
import { IPromptOptions, IPromptProfileMeta } from '../types/IPromptProfileMeta';
import { IPromptProfile } from '../types/prompt-profile';
import { BullhornPromptRestrictionsService, IUserRestrictionAttributes } from './bullhorn-prompt-restrictions.service';
import { PromptProfileService } from './prompt-profile.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {

  constructor(private http: HttpClient,
              private bullhornPromptRestrictionsService: BullhornPromptRestrictionsService,
              private promptProfileService: PromptProfileService) {}

  // TODO: get rid of all defaults once admin work is in place.

  // making two calls - one to the kyloe-ai database, one to the other databases
  public getFullConfigForCorpId(app: AIApps, configUrl: string, aiUrl: string, corpId: string): Observable<IClientConfig> {
    return this.getAiStatusForCorpId(aiUrl, corpId).pipe(
      concatMap(aiStatus =>
        this.getConfigForCorpId(configUrl, corpId).pipe(
          map(config => ({
            ...config,
            enabled: aiStatus.enabled,
            notetakerEnabled: aiStatus.notetakerEnabled,
            assistEnabledDate: aiStatus.assistEnabledDate,
            notetakerEnabledDate: aiStatus.notetakerEnabledDate,
            enabledModels: aiStatus.enabledModels,
          })),
          catchError(err => {
            if (err.status === 401 && this.isCorpNotFoundError(err)) {
              const namespace = corpId.split('-')[0];
              const defaultConfig: IClientConfig = {
                corpId,
                namespace,
                enabled: aiStatus.enabled,
                notetakerEnabled: aiStatus.notetakerEnabled,
                promptProfileMeta: {
                  options: this.getDefaultPromptOptions(),
                  baseHtmlStyleFile: undefined
                }
              };
              const isAssist = app === AIApps.ASSIST;
              const featureEnabled = isAssist
                ? aiStatus.enabled
                : aiStatus.notetakerEnabled;

              if (!featureEnabled) {
                return of(defaultConfig);
              }
              return (isAssist
                  ? this.saveAssistConfig(defaultConfig)
                  : this.saveNotetakerConfig(defaultConfig)
              ).pipe(map(() => defaultConfig));
            }
            return throwError(() => err);
          })
        )
      )
    );
  }

  public isCorpNotFoundError(err: any): boolean {
    return (
      err.error?.message === 'Corporation not found.'
    );
  }

  public saveNotetakerConfig(config: IClientConfig): Observable<any> {
    const corpIdParts = config.corpId?.split('-') || [];
    const payload = {
      corpId: config.corpId,
      app: 'NoteTaker',
      namespace: config.namespace,
      corporationId: corpIdParts[1],
      timestamp: Date.now()
    };
    return this.http.put<any>(`${environment.ntApiGatewayUrl}/config?corpId=${payload.corpId}`, payload);
  }

  public saveAssistConfig(config: IClientConfig): Observable<any> {
    const meta = config.promptProfileMeta ?? {
      options: this.getDefaultPromptOptions(),
      baseHtmlStyleFile: undefined
    };

    // Only send the filename (or null) into Dynamo
    const metaForDb = {
      options: meta.options,
      baseHtmlStyleFile: meta.baseHtmlStyleFile?.name ?? null
    };

    const payload = {
      corpId: config.corpId,
      sortKey: 'config',
      promptProfileMeta: JSON.stringify(metaForDb),
      enabled: true,
    };

    return this.http.put<any>(
      `${environment.apiGatewayUrl}/config?corpId=${payload.corpId}`,
      payload
    );
  }

  // this has gone back to what it was originally.
  public getConfigForCorpId(url: string, corpId: string): Observable<IClientConfig> {
    return this.http.get<any>(`${url}/config?corpId=${corpId}`).pipe(
      map((result: any) => {
        console.log(result);

        let clientConfig: IClientConfig = {
          DTAIEnabled: result.DTAIEnabled,
          corpId: corpId,
          enabled: result.enabled,
          namespace: result.namespace,
          promptProfileOrder: result.promptProfileOrder,
          promptProfileMeta: result.promptProfileMeta as IPromptProfileMeta,
          enableAiEditorPlugin: result.enableAiEditorPlugin
        };

        if ((clientConfig.promptProfileMeta && !clientConfig.promptProfileMeta.options) ||
            !clientConfig.promptProfileMeta) {
          clientConfig.promptProfileMeta = {
            options: this.getDefaultPromptOptions(),
            baseHtmlStyleFile: undefined
          };
        }
        if (result.app === AIApps.NOTETAKER) {
          clientConfig = {
            ...clientConfig,
            meta: result.meta as INoteTakerMeta,
            notetakerEnabled: result.enabled,
            saveTranscriptAsFile: result.saveTranscriptAsFile ?? true,
            oneNotePerCall: result.oneNotePerCall ?? false,
          };
        }
        return clientConfig;
      }),
      concatMap(clientConfig => {
        if (clientConfig.app === AIApps.NOTETAKER) {
          return of(clientConfig);
        }

        // ai-assist app needs the HTML style file name
        return this.getBaseHtmlStyleFilename().pipe(
          map(filename => {
            if (clientConfig.promptProfileMeta) {
              clientConfig.promptProfileMeta.baseHtmlStyleFile = {name: filename, file: undefined};
            }
            return clientConfig;
          })
        );
      })
    );
  }

  // call a new database to get the boolean values
  public getAiStatusForCorpId(url: string, corpId: string): Observable<IAIStatus> {
    return this.http.get<any>(`${url}/api/ai/config?corpId=${corpId}`).pipe(
      map(result => ({
        enabled: result.assist,
        notetakerEnabled: result.notetaker,
        assistEnabledDate: result.assistEnabledDate ? result.assistEnabledDate : null,
        notetakerEnabledDate: result.notetakerEnabledDate ? result.notetakerEnabledDate : null,
        enabledModels: result.enabledModels ? result.enabledModels : [],
      }))
    );
  }

  // The corp id for this method should be truncated so that it is only the corp number
  public getDatatoolsConfigForCorpId(corpId: string | undefined): Observable<IDataToolsConfig> {
    return this.http.get<any>(`${environment.dtApiGatewayUrl}/api/datarules/corporations/${corpId}`)
      .pipe(
        map((result: IDataToolsConfig) => {
          return result;
        }),
      );
  }

  public getAwesomeDocsConfigForCorpId(corpId: string | undefined): Observable<IAwesomeDocsConfig> {
    if (!corpId) {
      throw new Error('Invalid corpId');
    }
    const [namespace, clientName] = corpId.split('-');

    return this.http.get<any>(`${environment.adApiGatewayUrl}/api/adv2/client/${namespace}/${clientName}`)
      .pipe(
        map((result: IAwesomeDocsConfig) => {
          return result;
        }),
      );
  }

  public getPromptsForCorp(clientConfig: IClientConfig): Observable<IPromptProfile[]> {
    return this.http.get<any>(`${environment.apiGatewayUrl}/prompts/${clientConfig.corpId}`).pipe(
      map((result: IPromptProfile[]) => {
        result.forEach((prompt) => {
          this.promptProfileService.decodePromptFields(prompt);
          if (prompt.integration) {
            prompt.enabled = this.checkIntegrationEnabled(clientConfig, prompt.integration);
          }
        });
          return result;
        }
      )
    );
  }

  private checkIntegrationEnabled(clientConfig: IClientConfig, integration: string) {
    if (integration === 'datarules') {
      return clientConfig.DTAIEnabled;
    }
    if (integration === 'awesomedocs') {
      return clientConfig.ADAIEnabled;
    }
    return true;
  }

  public getPromptsByEntity(config: IClientConfig, entity: string): Observable<IPromptProfile[]> {
    return this.http.get<any>(`${environment.apiGatewayUrl}/prompts/${config.corpId}/list?entity=${entity}`).pipe(
      map((result: IPromptProfile[]) => {
        if (result && Array.isArray(result)) {
          const acceptedPrompts = [];
          for (const prompt of result) {
            try {
              this.saveNewModelToDB(prompt, config);
              acceptedPrompts.push(prompt);
            } catch (error) {
              console.error(`Skipping corrupted prompt - corpId: ${config.corpId}, promptId: ${prompt.promptId}:`, error);
            }
          }
          return acceptedPrompts;
        }
        return result;
      }),
      // Fetch current user's restriction attributes then filter prompts to only those the user is allowed to see
      switchMap(acceptedPrompts => this.bullhornPromptRestrictionsService.getCurrentUserAttributes().pipe(
        map(userAttrs => acceptedPrompts.filter(p => this.promptPassesRestrictions(p, userAttrs)))
      ))
    );
  }

  // converting from number to string (json parse)
  private promptPassesRestrictions(prompt: IPromptProfile, userAttrs: IUserRestrictionAttributes): boolean {
    if (prompt.allowedUserTypes?.length && !prompt.allowedUserTypes.some(
      allowedId => String(allowedId) === userAttrs.userTypeId)) {
      return false;
    }
    // iterate the allowed list, use includes cause of multiple fields
    if (prompt.allowedPrivateLabels?.length && !prompt.allowedPrivateLabels.some(
      allowedId => userAttrs.privateLabelIds.includes(String(allowedId)))) {
      return false;
    }
    if (prompt.allowedDepartments?.length && !prompt.allowedDepartments.some(
      allowedId => userAttrs.departmentIds.includes(String(allowedId)))) {
      return false;
    }
    return true;
  }

  private saveNewModelToDB(prompt: IPromptProfile, clientConfig: IClientConfig): IPromptProfile {
    this.promptProfileService.decodePromptFields(prompt);
    const oldModels = PromptProfileService.modelList.map((mapping) => mapping.oldModel);
    const containsOldModel = prompt.LLMs.some((model: string) => oldModels.includes(model));

    if (containsOldModel) {
      prompt.LLMs = this.promptProfileService.updateLLMs(prompt.LLMs);
      const promptToSave = this.promptProfileService.encodePromptFields(prompt);
      this.savePrompt(clientConfig, promptToSave).subscribe(() => {
        console.log(`Updated LLMs and sourceFields for prompt ${prompt.promptId} saved to database`);
      });
    }

    return prompt;
  }

  public getDefaultPrompts(): Observable<IPromptProfile[]> {
    return this.http.get<any>(`${environment.apiGatewayUrl}/prompts/kyloe`).pipe(
      map((result: IPromptProfile[]) => {
          result.forEach((prompt) => {
            return this.promptProfileService.transformPrompt(prompt);
          });
          return result;
        }
      )
    );
  }

  public savePrompt(clientConfig: IClientConfig, promptProfile: IPromptProfile): Observable<any> {
    return this.http.put<any>(`${environment.apiGatewayUrl}/prompts/${clientConfig.corpId}/${promptProfile.promptId}`, promptProfile).pipe(
      concatMap( () => {
        console.log('saved');
        return this.getPromptsForCorp(clientConfig);
      })
    );
  }

  public deletePrompt(clientConfig: IClientConfig, promptProfile: IPromptProfile) {
    return this.http.delete(`${environment.apiGatewayUrl}/prompts/${clientConfig.corpId}/${promptProfile.promptId}`).pipe(
      concatMap(() => {
         return this.getPromptsForCorp(clientConfig);
      })
    );
  }

  public save(clientConfig: IClientConfig): Observable<IClientConfig> {
    let putData = {
      corpId: clientConfig.corpId,
      promptProfileOrder: StringUtils.deserialize(clientConfig.promptProfileOrder),
      promptProfileMeta: '',
      enabled: clientConfig.enabled
    };
    if (clientConfig.promptProfileMeta) {
      // exclude the file property as we don't want it in the DynamoDB table
      const {baseHtmlStyleFile, ...metaWithoutFile} = clientConfig.promptProfileMeta;
      putData = {...putData, promptProfileMeta:  StringUtils.deserialize(metaWithoutFile)};

      if (clientConfig.promptProfileMeta.baseHtmlStyleFile?.file) {
        const file = clientConfig.promptProfileMeta.baseHtmlStyleFile.file;
        clientConfig.promptProfileMeta.baseHtmlStyleFile.file = undefined;

        return this.http
          .put<any>(`${environment.apiGatewayUrl}/config`, putData)
          .pipe(
            concatMap(() => this.uploadBaseHtmlStyleFileAsync(file)),
            concatMap(() => {
              return this.getFullConfigForCorpId(AIApps.ASSIST,
                environment.apiGatewayUrl, environment.dtApiGatewayUrl, clientConfig.corpId as string);
            })
          );
      }
    }
    return this.http
      .put<any>(`${environment.apiGatewayUrl}/config`, putData)
      .pipe(
        concatMap(() => {
          return this.getFullConfigForCorpId(AIApps.ASSIST,
            environment.apiGatewayUrl, environment.dtApiGatewayUrl, clientConfig.corpId as string);
        }),
      );
  }

  private getDefaultPromptOptions(): IPromptOptions {
    const getOptionsList = (array: IPromptDefaults[]): string[] => array.map(item => item.option);
    return {
      role: getOptionsList(SUGGESTED_ROLES),
      tone: getOptionsList(SUGGESTED_TONES),
      format: getOptionsList(SUGGESTED_FORMATS),
      resultLength: getOptionsList(SUGGESTED_RESULT_LENGTHS),
      explanationDifficulty: getOptionsList(SUGGESTED_EXPLANATION_DIFFICULTY),
      outputLanguage: getOptionsList(SUGGESTED_LANGUAGES)
    };
  }

  public sortPrompts(promptsArray: IPromptProfile[], config: IClientConfig): IPromptProfile[] {
    console.debug(promptsArray);
    if (config.promptProfileOrder) {
      promptsArray.sort((a, b) => {
        const indexA = config.promptProfileOrder.order.indexOf(a.promptId);
        const indexB = config.promptProfileOrder.order.indexOf(b.promptId);
        return indexA - indexB;
      });
    }
    console.debug(promptsArray);
    return promptsArray;
  }

  private getBaseHtmlStyleFilename(): Observable<string | undefined> {
    const obs$ = this.http.get(`${environment.kpHost}/ai-assist/config/file/prompt-base-html-style/name`, { responseType: 'text' });
    return obs$.pipe(catchError(err => {
      // just means there is no file for this tenant
      if (err.status === 404) {
        console.log('No base html style file found.');
        return of(undefined);
      }
      throw err;
    }));
  }

  private uploadBaseHtmlStyleFileAsync(file: File | undefined): Observable<any> {
    if (!file) {
      return of(null);
    }

    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${environment.kpHost}/ai-assist/config/file/prompt-base-html-style`, formData);
  }

  public downloadBaseHtmlStyleFile(): Observable<Blob> {
    return this.http.get(`${environment.kpHost}/ai-assist/config/file/prompt-base-html-style`, { responseType: 'blob' });
  }

  public getAiConfig(): Observable<IAiConfig> {
    return this.http.get<IAiConfig>(`${environment.kpHost}/ai-assist/config/ssm-params`);
  }
}

export interface IAiConfig {
  aiProvider: string;
  aiModel: string;
  temperature: number;
}
